import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { Done as DoneIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { crm, fmtDate, fmtDateTime, inr } from '../../../api/crm';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, TimeScale);

const Fact = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '—' : value}</Typography>
  </Box>
);

const Section = ({ title, children, action }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const ActivityChart = ({ activities }) => {
  const theme = useTheme();
  if (!Array.isArray(activities) || activities.length === 0) {
    return <Typography variant="body2" color="text.secondary">No activity data available</Typography>;
  }
  const sorted = [...activities].sort((a, b) => new Date(a.event_time_ist) - new Date(b.event_time_ist));
  const data = {
    datasets: [{
      label: 'User Activity',
      data: sorted.map((a) => ({ x: new Date(a.event_time_ist), y: 1, event: a.event_name })),
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
      pointRadius: 6,
      pointHoverRadius: 8,
      tension: 0.4,
      fill: false,
      borderWidth: 2
    }]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { type: 'time', time: { unit: 'hour', displayFormats: { hour: 'MMM d, HH:mm' } }, grid: { display: false }, ticks: { maxRotation: 45, minRotation: 45 } },
      y: { display: false, min: 0, max: 2 }
    },
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ctx.raw.event } } },
    interaction: { intersect: false, mode: 'index' }
  };
  return <Box sx={{ height: 240, width: '100%', position: 'relative' }}><Line data={data} options={options} /></Box>;
};

// LeadDetails is the expanded view of one lead: what is stored about them, their
// research unlocks, app analytics (loaded on demand) and the viewer's own follow-ups.
// readOnly (the support search) leaves the follow-ups out: they belong to sales people.
const LeadDetails = ({ lead, onFollowupChanged, readOnly = false }) => {
  const [followups, setFollowups] = useState(null);
  const [analytics, setAnalytics] = useState({ state: 'idle' });

  const loadFollowups = useCallback(async () => {
    if (readOnly) return;
    try {
      setFollowups(await crm(`followups/client/${lead.number}`));
    } catch (e) {
      setFollowups([]);
    }
  }, [lead.number, readOnly]);

  useEffect(() => { loadFollowups(); }, [loadFollowups]);

  const markDone = async (id) => {
    try {
      await crm(`followups/${id}`, { method: 'PATCH', body: { status: 'done' } });
      loadFollowups();
      if (onFollowupChanged) onFollowupChanged();
    } catch (e) {
      // keep the row; the list reloads on the next open
    }
  };

  const loadAnalytics = async () => {
    setAnalytics({ state: 'loading' });
    const q = { number: lead.number, profileID: lead.profile_id };
    const safe = (p) => p.catch(() => null);
    const [device, uninstall, activity] = await Promise.all([
      safe(crm('bigquery/user/device', { query: q })),
      safe(crm('bigquery/user/uninstall', { query: q })),
      safe(crm('bigquery/user/activity', { query: q })),
    ]);
    setAnalytics({ state: 'loaded', device: Array.isArray(device) ? device : [], uninstall: uninstall || {}, activity: Array.isArray(activity) ? activity : [] });
  };

  const unlocks = Array.isArray(lead.unlocks) ? lead.unlocks : [];
  const pnlColor = (v) => (v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.primary');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
      <Section title="Snapshot">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.5 }}>
          <Fact label="Email" value={lead.email} />
          <Fact label="Installed" value={fmtDateTime(lead.installed_at)} />
          <Fact label="App KYC" value={lead.kyc} />
          <Fact label="Demat KYC / DDPI" value={`${lead.demat_kyc ? 'Yes' : 'No'} / ${lead.ddpi ? 'Yes' : 'No'}`} />
          <Fact label="Onboarding step" value={(lead.onboarding_step || '').replace(/_/g, ' ')} />
          <Fact label="Source" value={[lead.source, lead.campaign].filter(Boolean).join(' · ')} />
          <Fact label="Algo plan" value={lead.algo_plan ? `${lead.algo_plan} (till ${fmtDate(lead.algo_plan_expiry)})` : ''} />
          <Fact label="Research plan" value={lead.research_plan ? `${lead.research_plan} (till ${fmtDate(lead.research_expiry)})` : ''} />
          <Fact label="Autopay" value={lead.autopay_active ? 'Active' : 'No'} />
          <Fact label="Payments" value={`${lead.paid_count || 0} · ${inr(lead.lifetime_paid)}`} />
          <Fact label="Last payment" value={lead.last_paid_at ? `${inr(lead.last_paid_amount)} · ${lead.last_paid_plan || ''} · ${fmtDate(lead.last_paid_at)}` : ''} />
          <Fact label="Last failed payment" value={lead.last_failed_at ? `${inr(lead.last_failed_amount)} · ${lead.last_failed_plan || ''} · ${fmtDateTime(lead.last_failed_at)}` : ''} />
          <Fact label="Live algos" value={lead.live_algos} />
          <Fact label="Capital in algos" value={lead.capital_deployed ? inr(lead.capital_deployed) : ''} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Gross P&L (all / 30d / 7d)</Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ color: pnlColor(lead.gross_pnl) }}>{inr(lead.gross_pnl)}</Box>{' / '}
              <Box component="span" sx={{ color: pnlColor(lead.gross_pnl_30d) }}>{inr(lead.gross_pnl_30d)}</Box>{' / '}
              <Box component="span" sx={{ color: pnlColor(lead.gross_pnl_7d) }}>{inr(lead.gross_pnl_7d)}</Box>
            </Typography>
          </Box>
          <Fact label="Last callback" value={lead.last_callback_at ? `${fmtDateTime(lead.last_callback_at)} · ${(lead.callback_source || '').replace('callback_card_', '').replace(/_/g, ' ')}` : ''} />
          <Fact label="Dealer" value={lead.dealer_name} />
          <Fact label="Webinar" value={lead.is_webinar_user ? `${lead.webinar_paid_amount ? inr(lead.webinar_paid_amount) + ' paid' : 'Registered'} · ${fmtDate(lead.webinar_registered_at)}` : ''} />
          <Fact label="Declared capital" value={lead.declared_capital} />
          <Fact label="Research unlocks" value={`${lead.free_usage || 0} free used · ${lead.unlock_count || 0} total`} />
          <Fact label="Region" value={lead.region} />
          <Fact label="Lead score" value={lead.lead_score ? Math.round(lead.lead_score) : ''} />
          <Fact label="Referred by" value={lead.referred_by} />
        </Box>
      </Section>

      {!readOnly && <Section title={`My follow-ups${followups && followups.length ? ` (${followups.length})` : ''}`}>
        {followups === null && <CircularProgress size={18} />}
        {followups && followups.length === 0 && <Typography variant="body2" color="text.secondary">None. Follow-ups you set are visible only to you.</Typography>}
        {(followups || []).map((fu) => (
          <Box key={fu.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Chip size="small" label={fu.status} color={fu.status === 'done' ? 'success' : new Date(fu.remind_at) < new Date() ? 'error' : 'default'} />
            <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>{fmtDateTime(fu.remind_at)} — {fu.note}</Typography>
            {fu.status !== 'done' && <IconButton size="small" color="success" onClick={() => markDone(fu.id)} title="Mark done"><DoneIcon fontSize="small" /></IconButton>}
          </Box>
        ))}
      </Section>}

      {unlocks.length > 0 && (
        <Section title={`Research unlocks (${unlocks.length})`}>
          <TableContainer sx={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Unlocked</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unlocks.map((u, i) => (
                  <TableRow key={i}>
                    <TableCell>{fmtDateTime(u.unlock_time)}</TableCell>
                    <TableCell>{(u.asset_names || []).join(', ')}</TableCell>
                    <TableCell>{(u.symbols || []).join(', ')}</TableCell>
                    <TableCell align="right">{(u.ltps || []).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {lead.is_app_user && (
        <Section
          title="App analytics"
          action={analytics.state !== 'loaded' && (
            <Button
              size="small"
              startIcon={analytics.state === 'loading' ? <CircularProgress size={14} /> : <VisibilityIcon />}
              disabled={analytics.state === 'loading'}
              onClick={loadAnalytics}
            >
              {analytics.state === 'loading' ? 'Loading' : 'Load'}
            </Button>
          )}
        >
          {analytics.state === 'loaded' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{analytics.uninstall.uninstall_status || 'App status unknown'}</Typography>
                {analytics.uninstall.latest_event && (
                  <Typography variant="caption" color="text.secondary">Last activity: {fmtDateTime(analytics.uninstall.latest_event)}</Typography>
                )}
              </Box>
              <Divider />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                {analytics.device.length === 0 && <Typography variant="body2" color="text.secondary">No device information</Typography>}
                {analytics.device.map((d, i) => <Fact key={i} label={d.Field} value={d.Value} />)}
              </Box>
              <Divider />
              <ActivityChart activities={analytics.activity} />
            </Box>
          )}
        </Section>
      )}
    </Box>
  );
};

export default LeadDetails;
